/**
 * KAN-650: поля прокси аккаунта (тип, адрес, порт, логин, пароль).
 * Только ввод: проверка и тело запроса в lib/telegramProxy.
 * KAN-655: строка провайдера, вставленная в "Адрес", раскладывается по полям.
 * KAN-713: types сужает список типов (справочник прокси: SOCKS5 и HTTP).
 */
import type { ChangeEvent, ClipboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  PROXY_PASTE_HINT,
  PROXY_TYPES,
  parseProxyString,
  type ProxyFormValues,
} from '@/lib/telegramProxy';
import type { ProxyType } from '@/types/telegramAccount';

interface Props {
  idPrefix: string;
  values: ProxyFormValues;
  onChange: (patch: Partial<ProxyFormValues>) => void;
  disabled?: boolean;
  types?: ReadonlyArray<{ value: ProxyType; label: string }>;
}

export function ProxyFields({
  idPrefix,
  values,
  onChange,
  disabled,
  types = PROXY_TYPES,
}: Props): JSX.Element {
  const id = (name: string): string => `${idPrefix}-${name}`;

  // Вставка: любая разобранная строка (включая host:port) уходит по полям.
  const pasteHost = (e: ClipboardEvent<HTMLInputElement>): void => {
    const parsed = parseProxyString(e.clipboardData.getData('text'));
    if (!parsed) return;
    e.preventDefault();
    onChange(parsed);
  };

  // Ввод: хост по буквам остаётся хостом; host:port по буквам не разбираем,
  // иначе после первой цифры порта остаток набора уходил бы в хост.
  const changeHost = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    const parsed = parseProxyString(value);
    const full = parsed && (parsed.username !== undefined || parsed.type !== undefined);
    onChange(full ? parsed : { host: value });
  };
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor={id('type')}>Тип</Label>
        <NativeSelect
          id={id('type')}
          data-testid={id('type')}
          value={values.type}
          disabled={disabled}
          options={types.map((t) => ({ value: t.value, label: t.label }))}
          onChange={(e) => onChange({ type: e.target.value as ProxyType })}
        />
      </div>
      <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor={id('port')}>Порт</Label>
        <Input
          id={id('port')}
          data-testid={id('port')}
          inputMode="numeric"
          autoComplete="off"
          placeholder="1080"
          value={values.port}
          disabled={disabled}
          onChange={(e) => onChange({ port: e.target.value.replace(/\D/g, '').slice(0, 5) })}
        />
      </div>
      <div className="col-span-2 flex flex-col gap-1">
        <Label htmlFor={id('host')}>Адрес</Label>
        <Input
          id={id('host')}
          data-testid={id('host')}
          autoComplete="off"
          placeholder="proxy.example.com или 1.2.3.4"
          aria-describedby={id('host-hint')}
          value={values.host}
          disabled={disabled}
          onPaste={pasteHost}
          onChange={changeHost}
        />
        <p id={id('host-hint')} className="text-xs text-gray-500">
          {PROXY_PASTE_HINT}
        </p>
      </div>
      <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor={id('username')}>Логин (необязательно)</Label>
        <Input
          id={id('username')}
          data-testid={id('username')}
          autoComplete="off"
          value={values.username}
          disabled={disabled}
          onChange={(e) => onChange({ username: e.target.value })}
        />
      </div>
      <div className="col-span-2 flex flex-col gap-1 sm:col-span-1">
        <Label htmlFor={id('password')}>Пароль (необязательно)</Label>
        <Input
          id={id('password')}
          data-testid={id('password')}
          type="password"
          autoComplete="new-password"
          value={values.password}
          disabled={disabled}
          onChange={(e) => onChange({ password: e.target.value })}
        />
      </div>
    </div>
  );
}
